// "Send your textures along": the designer's export instruction (a PDF, 2026-09-30) as the Help
// dialog shows it — one tab per program, a few steps each, a cropped screenshot of the one setting
// that matters with the spot ringed. Owner, 2026-09-30: a modal to start with, the crops cut from
// her PDF until she makes better ones, the English written here.

export type RenderExportApp = 'blender' | 'maya' | 'max' | 'painter' | 'usdz' | 'other';

// A ringed spot on a screenshot, in percent of its width and height.
export interface RenderGuideMark {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface RenderGuideShot {
  src: string;
  width: number;
  height: number;
  alt: string;
  marks: ReadonlyArray<RenderGuideMark>;
}

export interface RenderGuideStep {
  // The bold start: the action ('Pack the textures in.', 'File › Export › FBX (.fbx)').
  title: string;
  text?: string;
  shot?: RenderGuideShot;
  // The other way, in a grey box under the step.
  aside?: { title: string; text: string };
  // The closing "check it yourself": a tick instead of a number.
  check?: boolean;
}

export interface RenderGuideApp {
  id: RenderExportApp;
  label: string;
  intro?: string;
  steps: ReadonlyArray<RenderGuideStep>;
  // The step that packs the textures in: "Show me how" under the texture checklist opens it.
  packStep?: number;
  // That step in one line, for the checklist: "Next time, …".
  packHint?: string;
}

const SHOTS = 'assets/images/render/guide';

function shot(name: string, width: number, height: number, alt: string, ...marks: RenderGuideMark[]): RenderGuideShot {
  return { src: `${SHOTS}/${name}.webp`, width, height, alt, marks };
}

const CHECK: RenderGuideStep = {
  title: 'Check the file.',
  text: 'Import it into an empty scene. If the textures show, it’s ready.',
  check: true,
};

export const RENDER_EXPORT_GUIDE: ReadonlyArray<RenderGuideApp> = [
  {
    id: 'blender',
    label: 'Blender',
    packStep: 2,
    packHint: 'set Path Mode to Copy and turn on the embed button next to it.',
    steps: [
      {
        title: 'Check the materials.',
        text: 'In the Shading workspace, each texture should be an Image Texture node plugged into a Principled BSDF. Procedural nodes and Mix Shader don’t survive the export: bake them to images first.',
      },
      {
        title: 'File › Export › FBX (.fbx)',
        shot: shot('blender-menu', 700, 359, 'Blender’s File menu open at Export, with FBX (.fbx) at the bottom of the list', { left: 46.5, top: 87, width: 52.5, height: 9 }),
      },
      {
        title: 'Pack the textures in.',
        text: 'Set Path Mode to Copy, then turn on the embed button next to it. You get one file with the textures inside.',
        shot: shot('blender-path', 478, 160, 'Blender’s FBX export settings: Path Mode set to Copy, the embed button beside it turned on', { left: 41.5, top: 27, width: 56.5, height: 32 }),
        aside: { title: 'Rather keep them separate?', text: 'Set Path Mode to Relative or Strip Path, then drop the model’s whole folder.' },
      },
      {
        title: 'Transparent parts.',
        text: 'For glass, leaves or decals, connect the opacity map to the Alpha input of the Principled BSDF.',
        shot: shot('blender-alpha', 900, 392, 'Blender’s shader editor: an opacity map connected to Alpha on the Principled BSDF', { left: 33, top: 38, width: 34, height: 12 }),
      },
      { ...CHECK, text: 'Import it into an empty scene. If the textures show, it’s ready. We check the file again when you drop it.' },
    ],
  },
  {
    id: 'maya',
    label: 'Maya',
    packStep: 2,
    packHint: 'tick Embed Media in the FBX export options.',
    steps: [
      {
        title: 'Load the FBX plug-in.',
        text: 'In Windows › Settings/Preferences › Plug-in Manager, fbxmaya.mll should be Loaded.',
        shot: shot('maya-plugin', 449, 233, 'Maya’s Plug-in Manager with fbxmaya.mll loaded', { left: 2.5, top: 83, width: 95, height: 13 }),
      },
      {
        title: 'File › Export Selection…',
        text: 'Set File type to FBX export.',
        shot: shot('maya-export', 544, 385, 'Maya’s Export Selection Options with FBX export picked as the file type', { left: 30, top: 61.5, width: 28.5, height: 6.5 }),
      },
      {
        title: 'Embed the media.',
        text: 'Tick Embed Media. Under FBX File Format, choose Binary and FBX 2020.',
        shot: shot('maya-embed', 514, 290, 'Maya’s FBX export options: Embed Media ticked, type Binary, version FBX 2020', { left: 46, top: 8.6, width: 21.5, height: 9.3 }, { left: 39, top: 80, width: 56, height: 18.6 }),
      },
      CHECK,
    ],
  },
  {
    id: 'max',
    label: '3ds Max',
    packStep: 2,
    packHint: 'tick Embed Media in the FBX export dialog.',
    steps: [
      {
        title: 'Use Physical Material.',
        text: 'In the Material Editor, give the model Physical Materials with bitmap textures.',
      },
      {
        title: 'File › Export › Export Selected…',
        text: 'Save as FBX.',
        shot: shot('max-menu', 422, 330, '3ds Max’s File menu open at Export, with Export Selected highlighted', { left: 56, top: 41.5, width: 43, height: 8.5 }),
      },
      {
        title: 'Embed the media.',
        text: 'In the FBX Export dialog, under Include › Embed Media, tick Embed Media.',
        shot: shot('max-embed', 690, 320, '3ds Max’s FBX Export dialog with Embed Media ticked', { left: 49, top: 79.5, width: 15, height: 9.5 }),
      },
      CHECK,
    ],
  },
  {
    id: 'painter',
    label: 'Substance Painter',
    intro: 'Painter makes the textures but doesn’t attach them to the model. Attach them in a 3D app, then export the FBX from there.',
    steps: [
      { title: 'File › Export Textures.', text: 'A PBR Metallic Roughness preset works.' },
      { title: 'Connect them.', text: 'Plug the textures into the model’s material in Blender, Maya or 3ds Max.' },
      { title: 'Export the FBX.', text: 'Export it from that app with the textures packed in. Its tab shows how.' },
    ],
  },
  {
    id: 'usdz',
    label: 'USDZ',
    intro: 'A USDZ carries its textures inside, so there is nothing to set. To see what we’ll get, select the file on a Mac and press Space: Quick Look shows it in 3D.',
    steps: [],
  },
  {
    id: 'other',
    label: 'Other',
    intro: 'Cinema 4D, Houdini, ZBrush and the rest: in the FBX export, look for Embed Textures or Embed Media. No such option? Drop the model’s whole folder, with its textures.',
    steps: [],
  },
];

// What an FBX keeps and what it drops — the PDF's first page, at the end of every tab.
export const RENDER_FBX_KEEPS: ReadonlyArray<string> = ['Base color', 'Normal / Bump', 'Roughness', 'Metallic', 'Specular', 'Opacity', 'Ambient occlusion'];

export const RENDER_FBX_LOSES: ReadonlyArray<{ what: string; text: string; fix?: string }> = [
  { what: 'Procedural textures', text: 'Noise, gradients, generated patterns.', fix: 'Bake them to images.' },
  { what: 'Node math', text: 'Color Ramp, Mix RGB and the like.', fix: 'Bake it too.' },
  { what: 'Custom shaders', text: 'Mix Shader, Transparent BSDF.', fix: 'Use one standard shader.' },
  { what: 'Simulation maps', text: 'Smoke, fire, fluids, cloth.' },
  { what: 'Environment maps', text: 'And real-time reflections from your 3D app’s own engine.' },
];

// The program an FBX names (render-fbx-textures.ts) as a tab and a label: 'Blender (stable FBX IO)'
// + '2.76 (sub 0)' → Blender, 'Blender 2.76'; 'Maya' + '201800' → Maya, 'Maya 2018'. Anything else
// (mixamo.com, Cinema 4D) is Other under its own name.
export interface RenderExporter {
  app: RenderExportApp;
  label: string;
}

export function renderExporterOf(app: { name: string; version: string } | undefined): RenderExporter | undefined {
  if (!app?.name) {
    return undefined;
  }
  const id: RenderExportApp = /blender/i.test(app.name) ? 'blender' : /^maya/i.test(app.name) ? 'maya' : /3ds ?max/i.test(app.name) ? 'max' : 'other';
  const name = id === 'blender' ? 'Blender' : id === 'other' ? app.name.trim() : app.name.replace(/\s+/g, ' ').trim();
  // Maya writes its year with two more digits (201800); a version that is not a number is left out.
  const version = app.version.replace(/\s*\(sub \d+\)/i, '').trim().replace(/^(\d{4})\d{2}$/, '$1');
  return { app: id, label: /^\d+(\.\d+)*$/.test(version) ? `${name} ${version}` : name };
}

export function renderGuideApp(id: RenderExportApp): RenderGuideApp {
  return RENDER_EXPORT_GUIDE.find((app) => app.id === id) || RENDER_EXPORT_GUIDE[0];
}
